import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import type { MatchCard, Need, Service } from "@hub/shared";
import { Button, EmptyState, Spinner, toast } from "@/ui";
import { useEngineQuery } from "../_shims";
import { useDeleteItem, useEnsureSelfContact, useMatchCards, useSaveService, useSelfContact } from "../lib/hooks";
import { CompleteNeedDialog } from "../components/CompleteNeedDialog";
import { ItemForm, type ItemKind } from "../components/ItemForm";
import { NeedRow, ServiceRow } from "../components/ItemRow";
import { MatchModal } from "../components/MatchModal";
import { PageFrame } from "../components/PageFrame";
import { useMatchOpener } from "../components/useMatchOpener";
import s from "../network.module.css";

type Filter = "mine" | "people" | "active" | "completed";
const FILTERS: { id: Filter; label: string }[] = [
  { id: "mine", label: "Mine" },
  { id: "people", label: "My people" },
  { id: "active", label: "Active" },
  { id: "completed", label: "Completed" },
];

/** Needs and services share one page shell: my own items plus my contacts' items, filters, add/edit, complete. */
export function ItemsPage({ kind }: { kind: ItemKind }) {
  const [params, setParams] = useSearchParams();
  const [filters, setFilters] = useState<Set<Filter>>(() => new Set<Filter>(["active"]));
  const [editing, setEditing] = useState<{ open: boolean; contactId: string; need?: Need | null; service?: Service | null; forLabel?: string } | null>(null);
  const [completing, setCompleting] = useState<Need | null>(null);

  const self = useSelfContact();
  const ensureSelf = useEnsureSelfContact();
  const needs = useEngineQuery("listNeeds", { includeCompleted: true }, { enabled: kind === "need" });
  const services = useEngineQuery("listServices", { includeInactive: true }, { enabled: kind === "service" });
  const items: (Need | Service)[] = (kind === "need" ? needs.data : services.data) ?? [];
  const contactIds = useMemo(() => Array.from(new Set(items.map((i) => i.contactId))), [items]);
  const rows = useEngineQuery("getContactRows", { contactIds }, { enabled: contactIds.length > 0 });
  const nameOf = (contactId: string) => rows.data?.find((r) => r.contactId === contactId)?.displayName ?? null;
  const matches = useMatchCards();
  const opener = useMatchOpener();
  const del = useDeleteItem(kind);
  const saveService = useSaveService();
  const selfId = self.data?.contact.id ?? null;

  // Command bar and empty states deep-link here with ?new=1.
  useEffect(() => {
    if (params.get("new") !== "1") return;
    void (async () => {
      const id = await ensureSelf();
      setEditing({ open: true, contactId: id, forLabel: "me" });
      params.delete("new");
      setParams(params, { replace: true });
    })();
  }, [params, setParams, ensureSelf]);

  const toggle = (f: Filter) =>
    setFilters((prev) => {
      const next = new Set(prev);
      if (f === "mine" || f === "people") {
        const other: Filter = f === "mine" ? "people" : "mine";
        next.delete(other);
      }
      if (f === "active" || f === "completed") {
        const other: Filter = f === "active" ? "completed" : "active";
        next.delete(other);
      }
      if (prev.has(f)) next.delete(f);
      else next.add(f);
      return next;
    });

  const visible = items.filter((i) => {
    if (filters.has("mine") && i.contactId !== selfId) return false;
    if (filters.has("people") && i.contactId === selfId) return false;
    const done = "isCompleted" in i ? i.isCompleted : !(i as Service).isActive;
    if (filters.has("active") && done) return false;
    if (filters.has("completed") && !done) return false;
    return true;
  });

  const matchesFor = (item: Need | Service): MatchCard[] => {
    const cards = matches.data?.cards ?? [];
    return "isCompleted" in item ? cards.filter((c) => c.need.id === item.id) : cards.filter((c) => c.service.id === item.id);
  };

  const addForMe = async () => {
    const id = await ensureSelf();
    setEditing({ open: true, contactId: id, forLabel: "me" });
  };

  const loading = kind === "need" ? needs.isLoading : services.isLoading;
  const title = kind === "need" ? "Needs" : "Offers";

  return (
    <PageFrame
      title={title}
      subtitle={kind === "need" ? "What you and your people are looking for. Private unless you choose to share." : "What you and your people can offer. Private unless you choose to share."}
      actions={
        <Button variant="primary" icon={<Plus size={14} />} onClick={addForMe} data-testid="add-item">
          {kind === "need" ? "Add a need" : "Add an offer"}
        </Button>
      }
    >
      <div className={s.toolbar} role="group" aria-label="Filters">
        {FILTERS.map((f) => (
          <button key={f.id} type="button" className={s.filterButton} data-on={filters.has(f.id) ? "true" : undefined} aria-pressed={filters.has(f.id)} onClick={() => toggle(f.id)}>
            {f.label}
          </button>
        ))}
        <span className={`${s.small} ${s.muted}`}>{visible.length} of {items.length}</span>
      </div>

      {loading ? (
        <div className="u-row" style={{ justifyContent: "center", padding: 40 }}>
          <Spinner />
        </div>
      ) : visible.length === 0 ? (
        <EmptyState
          title={items.length === 0 ? (kind === "need" ? "No needs yet" : "No offers yet") : "Nothing matches these filters"}
          action={
            items.length === 0 ? (
              <Button variant="primary" onClick={addForMe}>
                {kind === "need" ? "Add what you need" : "Add what you offer"}
              </Button>
            ) : undefined
          }
        >
          {items.length === 0
            ? kind === "need"
              ? "Write down what you are looking for. Hub matches it against what people in your network can offer."
              : "List what you can help with. Hub matches it against needs across your network."
            : "Try another filter."}
        </EmptyState>
      ) : (
        <div className={s.stack}>
          {visible.map((item) =>
            "isCompleted" in item ? (
              <NeedRow
                key={item.id}
                need={item}
                ownerName={item.contactId === selfId ? "You" : nameOf(item.contactId)}
                matches={matchesFor(item)}
                onEdit={() => setEditing({ open: true, contactId: item.contactId, need: item, forLabel: item.contactId === selfId ? "me" : nameOf(item.contactId) ?? undefined })}
                onComplete={() => setCompleting(item)}
                onDelete={() => {
                  if (window.confirm(`Delete the need "${item.title}"?`)) del.mutate(item.id, { onSuccess: () => toast("Need deleted.") });
                }}
                onOpenMatch={opener.open}
                onMatchAction={opener.act}
              />
            ) : (
              <ServiceRow
                key={item.id}
                service={item}
                ownerName={item.contactId === selfId ? "You" : nameOf(item.contactId)}
                matches={matchesFor(item)}
                onEdit={() => setEditing({ open: true, contactId: item.contactId, service: item, forLabel: item.contactId === selfId ? "me" : nameOf(item.contactId) ?? undefined })}
                onToggleActive={() => saveService.mutate({ ...item, isActive: !item.isActive })}
                onDelete={() => {
                  if (window.confirm(`Delete the offer "${item.title}"?`)) del.mutate(item.id, { onSuccess: () => toast("Offer deleted.") });
                }}
                onOpenMatch={opener.open}
                onMatchAction={opener.act}
              />
            ),
          )}
        </div>
      )}

      {editing && <ItemForm kind={kind} contactId={editing.contactId} need={editing.need} service={editing.service} open={editing.open} forLabel={editing.forLabel} onClose={() => setEditing(null)} />}
      <CompleteNeedDialog need={completing} open={Boolean(completing)} onClose={() => setCompleting(null)} />
      <MatchModal card={opener.modal?.card ?? null} initialAction={opener.modal?.action ?? null} onClose={opener.close} />
    </PageFrame>
  );
}
